import { uniqueId } from 'lodash';
import qrcode from 'qrcode-generator';

import useTheme from '../composables/theme';
import { toast } from './message';

const { domain } = useTheme();

export const copy = (text) => {
  const showSuccess = () => toast('Copiado com sucesso!', 'success');

  const showError = () => toast('Erro ao tentar copiar', 'danger');

  const legacyCopy = () => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (copied) {
        showSuccess();
        return true;
      }
    } catch (err) {
      document.body.removeChild(textarea);
      return false;
    }

    document.body.removeChild(textarea);
    return false;
  };

  if (navigator && navigator.clipboard) {
    navigator.clipboard
      .writeText(text)
      .then(showSuccess)
      .catch(() => {
        if (!legacyCopy()) showError();
      });
  } else if (!legacyCopy()) {
    toast('Erro ao tentar copiar', 'danger');
  }
};

export const truncate = (text, length, clamp) => {
  const node = document.createElement('div');

  node.innerHTML = text;
  const content = node.textContent;

  return content.length > length ? content.slice(0, length) + clamp : content;
};

export const money = (value) => {
  if (Number.isNaN(parseFloat(value))) return '-';

  return parseFloat(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export const normalizeText = (text) => text
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

export const objHasProperty = (obj, key) => Object.hasOwn(obj, key);

export const isDomain = (param) => {
  const check = (text) => {
    const reg = new RegExp(`${text}*`, 'g');
    return reg.test(window.location.host);
  };

  if (typeof param === 'string') return check(param);

  return param.some(check);
};

export const validationLink = (token, scope = 'validacao') => {
  let prefix = 'assina';
  if (isDomain('dittobank')) prefix = 'fgts';
  if (isDomain('topmaistop')) prefix = 'sign';

  return `http://${prefix}.${domain()}/${scope}/${token}`;
};

export const hideCCBContract = () => isDomain(['poderfinanceiro']);

export const percent = (data) => (data * 100).toFixed(2);

export const formatBrazilianPhone = (phone) => {
  if (!phone) return phone;

  const digits = phone.toString().replace(/\D/g, '');

  // Telefone fixo: (XX) XXXX-XXXX
  if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }

  // Celular: (XX) 9XXXX-XXXX
  if (digits.length === 11 && digits[2] === '9') {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }

  // Não se encaixa em nenhum padrão
  return phone;
};

export const handleDownloadCSV = ({ data, name }) => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.setAttribute('href', url);
  a.setAttribute('download', `${name}.csv`);
  a.click();
};

export const isCurrentOrFutureDate = (date, now = new Date()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;

  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');

  return date >= today;
};

export const generateQRCode = ({ canvas, link }) => {
  try {
    const qr = qrcode(0, 'M');
    qr.addData(link);
    qr.make();

    // Draws QR code into existing canvas ref
    const size = 180;
    const ctx = canvas.getContext('2d');
    const cellCount = qr.getModuleCount();
    const cellSize = size / cellCount;

    canvas.width = size;
    canvas.height = size;
    ctx.clearRect(0, 0, size, size);

    for (let row = 0; row < cellCount; row++) {
      for (let col = 0; col < cellCount; col++) {
        ctx.fillStyle = qr.isDark(row, col) ? '#000000' : '#ffffff';
        ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
      }
    }

    return { qr, status: true };
  } catch {
    return { qr: null, status: false };
  }
};

export const generateRandomPassword = ({
  length = 16,
  numbers = true,
  symbols = true,
  uppercase = true,
  lowercase = true,
  excludeSimilarCharacters = true,
} = {}) => {
  const groups = {
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    numbers: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
  };

  const similar = 'ilLIoO01';

  const enabledGroups = [];

  if (lowercase) enabledGroups.push(groups.lowercase);
  if (uppercase) enabledGroups.push(groups.uppercase);
  if (numbers) enabledGroups.push(groups.numbers);
  if (symbols) enabledGroups.push(groups.symbols);

  if (!enabledGroups.length) {
    throw new Error('At least one character group must be enabled');
  }

  const sanitize = (str) => (
    excludeSimilarCharacters
      ? [...str].filter((c) => !similar.includes(c)).join('')
      : str
  );

  const sanitizedGroups = enabledGroups.map(sanitize);
  const allChars = sanitizedGroups.join('');

  if (length < sanitizedGroups.length) {
    throw new Error(
      `length must be at least ${sanitizedGroups.length}`,
    );
  }

  const getRandomChar = (chars) => {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);

    return chars[array[0] % chars.length];
  };

  const password = [];

  // garante pelo menos um de cada grupo
  sanitizedGroups.forEach((group) => {
    password.push(getRandomChar(group));
  });

  // completa o restante
  while (password.length < length) {
    password.push(getRandomChar(allChars));
  }

  // embaralha
  for (let i = password.length - 1; i > 0; i -= 1) {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);

    const j = array[0] % (i + 1);

    [password[i], password[j]] = [password[j], password[i]];
  }

  return password.join('');
};

export const getUniqueID = () => uniqueId(`${Math.floor(Math.random() * 1000000)}_`);

export const formatCNPJ = (cnpj) => {
  const onlyNumbers = cnpj.replace(/\D/g, '');

  if (onlyNumbers.length !== 14) return cnpj;

  return onlyNumbers.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5',
  );
};

export const formatCurrencyBR = (value) => parseFloat(value).toLocaleString(
  'pt-BR',
  {
    style: 'currency',
    currency: 'BRL',
  },
);
